import { Component, OnInit } from '@angular/core';
import { EntityService } from '../../services/entity.service';
import { Constants } from '../../models/constants.class';
import { Psd2Service } from '../../services/psd2.service';
import { Router } from '@angular/router';
import { AggregatioRequestInterface } from '../../models/aggregation.interface';
import { Location } from '@angular/common';
import { TealiumService } from '@app/services/tealium.service';
import { IEntitiesState } from '../ag-entities-loader/ag-entities-loader.component';
/**
 * @description Component
 * @class AgMessagesComponent
 * @implements {OnInit} OnInit
 */
@Component({
  standalone: false,
  selector: 'app-ag-messages',
  templateUrl: './ag-messages.component.html',
  styleUrls: ['./ag-messages.component.scss'],
})
export class AgMessagesComponent implements OnInit {
  public constants = Constants;
  public step = 1;
  public error = 0;
  public doingAggregation = false;
  public aggregationDone = false;
  public currentCall: Promise<void> | undefined = undefined;
  public emergentWindow: any;
  public windowState = 0;
  public canGoBack = true;
  public paramsString: any = '';
  public params: any[] = [];

  /**
   * @description Creates an instance of AgMessagesComponent.
   * @param router Router
   * @param entityService EntityService
   * @param psd2Service Psd2Service
   * @param _location Location
   * @param _tealiumService TealiumService
   */
  constructor(
    public readonly router: Router,
    public readonly entityService: EntityService,
    public readonly psd2Service: Psd2Service,
    private readonly _location: Location,
    private readonly _tealiumService: TealiumService,
  ) {
    this.paramsString = sessionStorage.getItem(Constants.url.params);
    this.params = JSON.parse(this.paramsString);
  }

  /**
   * @description ngOnInit
   */
  ngOnInit(): void {
    // In webview mode, the consentId may have been saved to sessionStorage
    // by callback-login.html but PSD2 service may not have picked it up
    // (e.g. if the service was already instantiated before the page reload).
    const consentIdFromSession = sessionStorage.getItem('consentId');
    if (consentIdFromSession && this.psd2Service.getConsentCode() === '') {
      this.psd2Service.setConsentCode(consentIdFromSession);
    }

    if (this.psd2Service.getConsentCode() === '') {
      this.exit();
      return;
    }

    try {
      this.setConsentCodeInEntitiesStateEntity();
    } catch (e) {
      console.error('[Trusso] Error setting consent code in entitiesState:', e);
    }
    this.exit();
  }

  /**
   * @description getSelectedEntityName
   * @returns (string | undefined)
   */
  getSelectedEntityName(): string | undefined {
    return this.entityService.getSelectedEntity().name;
  }

  /**
   * @description incrementStep
   * @deprecated Increment step is made in loader view
   */
  incrementStep(): void {
    this.step++;

    if (this.step === 2 && !this.aggregationDone) {
      this.aggregationConsent(this.psd2Service.getConsentCode());
    }
  }

  /**
   * @description isAggregationComplete
   * @returns boolean
   * @deprecated
   */
  isAggregationComplete(): boolean {
    return this.aggregationDone;
  }

  /**
   * @description manageErrorResponse
   * @param errorResponse any
   * @deprecated
   */
  manageErrorResponse(errorResponse: any): void {
    this.currentCall = undefined;
    this.step = -1;
    this.error = 500;
    this._tealiumService.sendError({
      category: 'Server error',
      action: 'error',
      data: {
        cod_error: errorResponse.status,
        desc_error: 'popup window',
      },
    });
  }

  /**
   * @description tealiumAggregationComplete
   * @deprecated
   */
  tealiumAggregationComplete(): void {
    this._tealiumService.sendPageLoad({
      page_name:
        '/PSD2/agregar_banco/conexión_resumen/' +
        `${this.entityService.getSelectedEntity().type || ''}`,
      page_title:
        'PSD2 agregar banco conexión resumen ' +
        `${this.entityService.getSelectedEntity().type || ''}`,
      page_type: 'thank you',
      product_scope: 'operativa_no_financiera',
      product_category: 'PSD2',
      product_name: 'crédito digital',
      page_category: 'servicing',
      added_bank: this.getSelectedEntityName(),
    });
  }

  /**
   * @description aggregationConsent
   * @param consent any
   * @deprecated postAggregationConsent is made in progress view to every entity.
   */
  aggregationConsent(consent: any): void {
    this.doingAggregation = true;
    const aggregationRequest: AggregatioRequestInterface = {};
    const products = this.entityService.getSelectedEntity().products || [];
    aggregationRequest.aspspcode = this.entityService.getSelectedEntity().code;
    aggregationRequest.products = products
      .filter((value) => value.type === Constants.entityType.psd2)
      .map((value) => value.name || '');
    aggregationRequest.consentCode = consent;
    aggregationRequest.expedientCode =
      sessionStorage.getItem('prop-unique-id') || '';
    this.currentCall = this.psd2Service
      .postAggregationConsent(aggregationRequest, 'consentId')
      .then((response) => {
        this.doingAggregation = false;
        this.step = 2;
        this.aggregationDone = true;
        this.tealiumAggregationComplete();
      })
      .catch((errorResponse) => {
        this.manageErrorResponse(errorResponse);
      });
  }

  /**
   * @description exit
   * @returns Promise<boolean>
   */
  exit(): Promise<boolean> {
    if (sessionStorage.getItem('entitiesState')) {
      return this.router.navigate(['loader'], { queryParamsHandling: 'merge' });
    }

    return this.router.navigate([Constants.url.index]);
  }

  /**
   * @description retry
   * @deprecated retry behaviour is available in progress view.
   */
  retry(): void {
    this.step = 1;
    this.error = 0;
    this.windowState = 0;
    this.doingAggregation = false;
    this.aggregationDone = false;
    this.incrementStep();
  }

  /**
   * @description hasAnotherProducts
   * @returns (string | undefined)
   * @deprecated not in use
   */
  hasAnotherProducts(): string | undefined {
    const entitySelectedProducts =
      this.entityService.getSelectedEntity().products || [];
    const anotherProducts = entitySelectedProducts.find(
      (product) =>
        product.type !== this.entityService.getSelectedEntityProductType(),
    );
    return anotherProducts && anotherProducts.type;
  }

  /**
   * @description goToAnotherProducts
   * @deprecated not in use
   */
  goToAnotherProducts(): void {
    Promise.resolve(this.router.navigate([Constants.url.index]));
  }

  /**
   * @description back
   */
  back(): void {
    this._location.back();
  }

  /**
   * This method replies the same web flow, consent code is setted inside current entity being processed
   * and then, progress view is in charge of request postAggregationConsent to every
   * entity.
   */
  setConsentCodeInEntitiesStateEntity(): void {
    const encodedEntitiesState: string | null =
      sessionStorage.getItem('entitiesState');
    if (!encodedEntitiesState) {
      console.warn(
        '[Trusso] No entitiesState found in sessionStorage, skipping consent code update',
      );
      return;
    }
    const parsedEntitiesState: IEntitiesState =
      this.parseBase64EntitiesState(encodedEntitiesState);
    let entities = parsedEntitiesState.entities;
    const currentEntityIndex = parsedEntitiesState.index;

    if (parsedEntitiesState.retryId) {
      const retryId = parsedEntitiesState.retryId;
      const consentCode = this.psd2Service.getConsentCode();
      console.warn('[Trusso] RetryId ', retryId);
      console.warn('[Trusso] consentCode', consentCode);
      entities = entities.map((el: any) => {
        if (el?.id?.toString() === retryId?.toString()) {
          return { ...el, consentCode };
        }
        return el;
      });
      console.warn('[Trusso] Entities Modified Array', entities);
    } else {
      entities[currentEntityIndex].consentCode =
        this.psd2Service.getConsentCode();
    }

    parsedEntitiesState.entities = entities; // Redundand cause is a mutable array.
    console.warn('[Current state]', parsedEntitiesState);
    sessionStorage.setItem(
      'entitiesState',
      this.encodeBASE64EntitiesState(parsedEntitiesState),
    );
  }

  /**
   * Parse received base64 entities state. Only applies to mobile. Not valid in web.
   * @param base64EntitiesState
   * @returns IEntitiesState current state.
   */
  parseBase64EntitiesState(base64EntitiesState: string): IEntitiesState {
    return JSON.parse(atob(base64EntitiesState));
  }

  /**
   * Get entity state in base64 string. Only applies to mobile. Not valid in web.
   * @param currentEntityCode
   * @returns string Base64 entity state.
   */
  encodeBASE64EntitiesState(entitiesState: IEntitiesState): string {
    return btoa(JSON.stringify(entitiesState));
  }
}
